import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const OnboardingFlowController = () => {
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState('');

  useEffect(() => {
    const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding');
    const savedUserType = localStorage.getItem('userType');
    
    if (!hasCompletedOnboarding || !savedUserType) {
      setShowOnboarding(true);
    }
  }, []);

  const userTypes = [
    {
      id: 'parent',
      label: 'Parent/Carer',
      description: 'Tracking symptoms for a child or dependent',
      icon: 'Users'
    },
    {
      id: 'elderly',
      label: 'Elderly Patient',
      description: 'Managing personal health conditions',
      icon: '/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png'
    },
    {
      id: 'chronic',
      label: 'Chronic Condition',
      description: 'Long-term condition monitoring',
      icon: 'Activity'
    },
    {
      id: 'general',
      label: 'General Adult',
      description: 'General health tracking',
      icon: 'User'
    }
  ];

  const handleUserTypeSelect = (typeId) => {
    setSelectedUserType(typeId);
  };

  const handleContinue = () => {
    if (selectedUserType) {
      localStorage.setItem('userType', selectedUserType);
      localStorage.setItem('hasCompletedOnboarding', 'true');
      setShowOnboarding(false);
      navigate('/');
    }
  };

  const handleSkip = () => {
    localStorage.setItem('userType', 'general');
    localStorage.setItem('hasCompletedOnboarding', 'true');
    setShowOnboarding(false);
    navigate('/');
  };

  if (!showOnboarding) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-background z-[1000] flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div className="w-full max-w-4xl mx-auto py-8">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-xl mb-4 sm:mb-6">
            <img src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png" alt="MyBodyToday Logo" className="w-12 h-12 sm:w-16 sm:h-16 object-contain" />
          </div>
          <h1 id="onboarding-title" className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-foreground mb-3 sm:mb-4">
            Welcome to MyBodyToday
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Help us personalize your experience by selecting your user type
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {userTypes?.map((type) => {
            const isSelected = selectedUserType === type?.id;
            return (
              <button
                key={type?.id}
                onClick={() => handleUserTypeSelect(type?.id)}
                className={`
                  flex flex-col items-start p-6 sm:p-8 rounded-xl border-2
                  min-h-[140px] sm:min-h-[160px]
                  transition-all duration-300 ease-out
                  transform hover:scale-[1.02] active:scale-[0.97]
                  ${isSelected 
                    ? 'border-primary bg-primary/5 shadow-therapeutic-md ring-2 ring-primary/20 ring-offset-2 ring-offset-background scale-[1.01]' 
                    : 'border-border bg-card hover:border-primary/60 hover:shadow-therapeutic hover:bg-primary/[0.02]'
                  }
                  focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                `}
                aria-pressed={isSelected}
              >
                <div className={`
                  flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-lg mb-4
                  transition-all duration-300 ease-out
                  ${isSelected 
                    ? 'bg-primary text-primary-foreground shadow-md scale-110' 
                    : 'bg-muted text-muted-foreground'
                  }
                `}>
                  <Icon name={type?.icon} size={24} strokeWidth={2} />
                </div>
                <h3 className={`
                  text-lg sm:text-xl font-heading font-semibold mb-2
                  transition-colors duration-200
                  ${isSelected ? 'text-primary' : 'text-foreground'}
                `}>
                  {type?.label}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground text-left">
                  {type?.description}
                </p>
                <div className={`
                  mt-auto pt-4 flex items-center gap-2
                  transition-all duration-300 ease-out
                  ${isSelected ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none'}
                `}>
                  <Icon name="CheckCircle2" size={20} color="var(--color-primary)" strokeWidth={2.5} />
                  <span className="text-xs font-semibold text-primary">Selected</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
          <Button
            variant="outline"
            size="lg"
            onClick={handleSkip}
            className="w-full sm:w-auto min-w-[160px]"
          >
            Skip for now
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={handleContinue}
            disabled={!selectedUserType}
            iconName="ArrowRight"
            iconPosition="right"
            className="w-full sm:w-auto min-w-[160px]"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlowController;